import { Divider } from '@components/core-ui/Divider';
import { InputUpload } from '@components/FileUpload/components/InputUpload';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import MinusIcon from '@components/icons/Minus.svg';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation } from '@tanstack/react-query';
import { OrganizationsService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import Papa from 'papaparse';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { Content } from './styles/Content';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
};

interface IRow {
  email: string;
  teamIds: string[];
}

interface ICsvRow {
  Email: string;
  'Team 1': string;
  'Team 2': string;
  'Team 3': string;
  'Team 4': string;
  'Team 5': string;
}

export const InviteUsersByCsvModal = ({ onCancel, onSuccess }: TProps) => {
  const [data, setData] = useState<IRow[]>([]);
  const { call } = useAPI();

  const { organization } = useAuthContext();

  const createInvitationsMutation = useMutation({
    mutationFn: (invitations: IRow[]) =>
      call(() => OrganizationsService.createInvitations({ oid: organization?.id ?? '', requestBody: { invitations } })),
    onSuccess: () => {
      onSuccess();
      toast.success('Invitations have been sent');
    },
    onError: notifyApiCallError,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createInvitationsMutation.mutate(data);
  };

  const firstOptions = organization?.orgTeams ?? [];

  const teamsIdsByNameMap = new Map((firstOptions || []).map((role) => [role.name.toLowerCase(), role.id]));
  const teamsNamesByIdMap = new Map((firstOptions || []).map((role) => [role.id, role.name]));

  const parseCSV = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }: { data: ICsvRow[] }) => {
        const validatedData = data
          .map((row: ICsvRow) => {
            const email = row.Email;

            const teamIds = [
              teamsIdsByNameMap.get(row['Team 1']?.toLowerCase()),
              teamsIdsByNameMap.get(row['Team 2']?.toLowerCase()),
              teamsIdsByNameMap.get(row['Team 3']?.toLowerCase()),
              teamsIdsByNameMap.get(row['Team 4']?.toLowerCase()),
              teamsIdsByNameMap.get(row['Team 5']?.toLowerCase()),
            ].filter((value) => !!value);

            return { email, teamIds };
          })
          .filter((row) => !!row.email && row.teamIds.length > 0);

        if (!validatedData.length) {
          toast.error('Please, set your CSV file according to requirements and try again');
          return;
        }

        const uniqueData: IRow[] = Object.values(
          validatedData.reduce((acc: Record<string, IRow>, obj) => {
            acc[obj.email] = acc[obj.email] || obj;
            return acc;
          }, {}),
        );

        setData(uniqueData.slice(0, 50) || []);
      },
    });
  };

  return (
    <ModalWrapper width="600px">
      <ModalTitle>Invite Members By CSV</ModalTitle>
      <Content>
        <form action="#" onSubmit={handleSubmit}>
          <div className="inputWrapper">
            {!data.length && (
              <a
                href="/example_invitation.csv"
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '0.75rem',
                  color: 'var(--color-link)',
                }}
              >
                Example CSV
              </a>
            )}
            {!data.length && (
              <InputUpload
                accept=".csv"
                onFiles={(files) => {
                  if (files.length) {
                    parseCSV(files[0]);
                  }
                }}
                multiple={false}
              />
            )}

            {data.map((row, index) => {
              const teamNames = row.teamIds?.map((id) => teamsNamesByIdMap.get(id)).join(', ');
              const counter = index + 1;
              return (
                <div key={row.email}>
                  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    {`${counter}. ${row.email} `}
                    {!!teamNames.length && `[${teamNames}]`}
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setData((prev) => prev.filter((value) => value.email !== row.email));
                      }}
                    >
                      <MinusIcon />
                    </div>
                  </div>
                  {index !== data.length - 1 && <Divider />}
                </div>
              );
            })}
          </div>

          <div className="note-text">* Note: you can invite a maximum of 50 members at once.</div>

          <div className="buttonWrapper">
            <OutlinedButton style={{ width: '130px' }} onClick={onCancel}>
              Cancel
            </OutlinedButton>

            <Button
              type="submit"
              style={{ width: '130px' }}
              disabled={!data.length || createInvitationsMutation.isPending}
            >
              Invite Members
            </Button>
          </div>
        </form>
      </Content>
    </ModalWrapper>
  );
};
